import { Button, Checkbox, Form, Icon, Input } from 'semantic-ui-react';

import { POLL_MAX_OPTIONS, POLL_MIN_OPTIONS, POLL_OPTION_MAX_LENGTH, type PollDraft } from '../utils/poll';

/**
 * The answers and settings of a poll, shared by the Content composer and the /leadership one.
 * The question itself is the post's title, and lives in the composer.
 */
export function PollEditor({ value, onChange }: { value: PollDraft; onChange: (next: PollDraft) => void }) {
    const setOption = (index: number, text: string) =>
        onChange({ ...value, options: value.options.map((o, i) => (i === index ? text : o)) });
    const removeOption = (index: number) =>
        onChange({ ...value, options: value.options.filter((_, i) => i !== index) });
    const addOption = () => onChange({ ...value, options: [...value.options, ''] });

    return (
        <div style={{ marginBottom: '1em' }}>
            <Form.Field required>
                <label>Answers</label>
                {value.options.map((option, index) => (
                    <div key={index} style={{ display: 'flex', gap: '0.5em', marginBottom: '0.5em', alignItems: 'center' }}>
                        <Input
                            fluid
                            style={{ flex: 1 }}
                            placeholder={`Answer ${index + 1}`}
                            value={option}
                            maxLength={POLL_OPTION_MAX_LENGTH}
                            onChange={(_, { value: text }) => setOption(index, String(text))}
                        />
                        <Button
                            type="button"
                            basic
                            icon="trash"
                            aria-label={`Remove answer ${index + 1}`}
                            disabled={value.options.length <= POLL_MIN_OPTIONS}
                            onClick={() => removeOption(index)}
                        />
                    </div>
                ))}
                <Button
                    type="button"
                    basic
                    size="small"
                    icon="plus"
                    content="Add answer"
                    disabled={value.options.length >= POLL_MAX_OPTIONS}
                    onClick={addOption}
                />
            </Form.Field>

            <Form.Field>
                <Checkbox
                    toggle
                    label="Let people choose more than one answer"
                    checked={value.allowMultiple}
                    onChange={(_, { checked }) => onChange({ ...value, allowMultiple: checked === true })}
                />
            </Form.Field>
            <Form.Field>
                <Checkbox
                    toggle
                    label={'Show a "Something else" button, so people can write their own answer'}
                    checked={value.allowOther}
                    onChange={(_, { checked }) => onChange({ ...value, allowOther: checked === true })}
                />
            </Form.Field>
            <Form.Field>
                <label>Stop taking answers at (optional)</label>
                <Input
                    type="datetime-local"
                    value={value.closesAt}
                    onChange={(_, { value: closesAt }) => onChange({ ...value, closesAt: String(closesAt) })}
                />
            </Form.Field>
            <div style={{ fontSize: '0.85em', color: 'rgba(0,0,0,.6)' }}>
                <Icon name="eye" /> Answers are not anonymous — you&apos;ll see who chose what under
                Content → Polls. People are told this in the app.
            </div>
        </div>
    );
}
